"use client";
import { useDebounceValue } from "usehooks-ts";

import { type InfoLineProps } from "#/components/InfoLine/InfoLine";
import { tooltips } from "#/config/tooltips";
import { usePerpInstrumentName } from "#/hooks/usePerpInstrumentName";
import { useCalculateUserRisk } from "#/services/kyan/hooks/useCalculateUserRisk";
import { usePerpIsBuy } from "#/store/perps/tradePanel/perpIsBuy";
import { usePerpSize } from "#/store/perps/tradePanel/perpSize";
import { usePerpTradeType } from "#/store/perps/tradePanel/perpTradeType";
import { useAccountState } from "#/store/wallet/account/accountState";
import { getImpactColor } from "#/utils/getImpactColor";
import { getMmrImr } from "#/utils/getMmrImr";
import { getPerpInstrumentProperties } from "#/utils/instrument.utils";

export const usePerpPortfolioImpactController = () => {
  const { perpSize } = usePerpSize();
  const { perpInstrumentName } = usePerpInstrumentName();
  const { perpIsBuy } = usePerpIsBuy();
  const { isRFQ: skipImpact } = usePerpTradeType();

  const [debouncedSize] = useDebounceValue(perpSize, 500);

  const isValid = !skipImpact;

  const { im, mm, greeks, isLoading } = useCalculateUserRisk({
    inputs: [
      {
        instrument: perpInstrumentName,
        size: debouncedSize,
        isBuy: perpIsBuy,
        isEnabled: isValid,
      },
    ],
    option: null,
  });

  const { underlying } = getPerpInstrumentProperties(perpInstrumentName);

  const { portfolioState } = useAccountState(underlying);

  const equity = portfolioState?.equity ?? 0;

  const isMissingDetails = !debouncedSize;

  const { imr, mmr } = getMmrImr({ equity, im, mm });

  const marginData: InfoLineProps[] = [
    {
      data: {
        label: "Margin Usage (IMΔ)",
        amount: im - (portfolioState?.im ?? 0),
        unit: "USD",
      },
      tooltip: tooltips.marginUsage,
    },
    {
      data: {
        label: "Available Margin",
        amount: equity - im,
        unit: "USD",
      },
      tooltip: tooltips.availableMargin,
    },
    {
      data: {
        label: "IMr",
        change: {
          from: (portfolioState?.imr ?? 0) * 100,
          to: imr * 100,
          template: "0.00%",
        },
      },
      leftLabelClassName: getImpactColor(portfolioState?.imr ?? 0),
      rightLabelClassName: getImpactColor(imr),
      tooltip: tooltips.IMr,
    },
    {
      data: {
        label: "MMr",
        change: {
          from: (portfolioState?.mmr ?? 0) * 100,
          to: mmr * 100,
          template: "0.00%",
        },
      },
      leftLabelClassName: getImpactColor(portfolioState?.mmr ?? 0),
      rightLabelClassName: getImpactColor(mmr),
      tooltip: tooltips.MMr,
    },
    {
      data: {
        label: "Δ Delta",
        change: {
          from: portfolioState?.greeks.delta ?? 0,
          to: greeks.delta ?? 0,
          template: "0.0000",
        },
      },
      tooltip: tooltips.Delta,
    },
  ];

  return {
    isValid,
    marginData,
    isLoading,
    isMissingDetails,
  };
};
